import Cookies from 'universal-cookie';
import { useTranslation } from 'react-i18next';
import { createSearchParams, generatePath, useNavigate } from 'react-router-dom';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { faHeartCircleCheck } from '@fortawesome/pro-solid-svg-icons/faHeartCircleCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { SitterCard } from 'us-react-components/dist/SitterCard/SitterCard';
import { SitterCardSkeleton } from 'us-react-components/dist/SitterCard/SitterCardSkeleton';
import { Sitter } from 'us-web-services';
import { staticUrls } from '../../../../../constants/StaticUrls';
import { Storage } from '../../../../../constants';
import { useDependencies } from '../../../../../dependencies';
import { ModalName } from '../../../../../modals/constants';
import { useModal } from '../../../../../modals/modalContext';
import { Routes } from '../../../../../Routes';
import { Button, ButtonType } from '../../../../Button';
import { FavoritesNullState } from './FavoritesNullState';

type Props = {
  bestMatchSitters: Sitter[];
  city?: string;
  count?: number;
  loading?: boolean;
  pictureUrl?: string;
  sitters: Sitter[];
  state?: string;
  zip?: string;
  navigateToSearchPage(bookingVerticalTypeId: number | undefined): void;
  setFavorite(isFavorited: boolean, favoriteId: number, sitterId: number): Promise<void>;
}

const SKELETONS = [
  <SitterCardSkeleton key='skeleton-1' />,
  <SitterCardSkeleton key='skeleton-2' />,
];

export const Favorites = ({
  bestMatchSitters = [],
  city = '',
  count = 0,
  loading = true,
  pictureUrl,
  sitters = [],
  state = '',
  zip = '',
  navigateToSearchPage,
  setFavorite,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const cookies = new Cookies();
  const navigate = useNavigate();
  const { services: { tracker }, stores } = useDependencies();
  const { user } = stores.user;
  const { verification } = stores;
  const modal = useModal();

  const handleClick = (id: number): void => {
    const route = generatePath(Routes.sitter.profile, { id: `${id}` });
    navigate(route);
  };

  const showSitters = sitters.slice(0, 4);
  const bestMatchPictureUrls = bestMatchSitters.map(s => s.pictureUrl);
  const familyPictureUrl = pictureUrl && pictureUrl?.endsWith('us-default-avatar-320.png') ? staticUrls.parentDefault : pictureUrl;

  const imageUrls: string[] = [
    familyPictureUrl ?? '',
    bestMatchPictureUrls[0] ?? staticUrls.caregiver1,
    bestMatchPictureUrls[1] ?? staticUrls.caregiver2,
    bestMatchPictureUrls[2] ?? staticUrls.caregiver3,
    bestMatchPictureUrls[3] ?? staticUrls.caregiver4,
    bestMatchPictureUrls[4] ?? staticUrls.caregiver5,
    bestMatchPictureUrls[5] ?? staticUrls.caregiver6,
  ];

  const trackEvent = async (event: string, params?: {elementText: string}): Promise<void> => tracker.track(event, params);

  const openIdentityCheckModal = (): void => {
    modal?.setModalName(ModalName.IdentityVerification);
  };

  const continueToBook = (sitterId: number): void => {
    cookies.set(Storage.BOOKING_ORIGIN, '/family/dashboard', {
      path: '/',
    });

    navigate({
      pathname: generatePath(Routes.parent.bookSitter),
      search: `${createSearchParams({
        city,
        userId: (user.id || 0).toString(),
        parentId: (user.id || 0).toString(),
        sitterId: sitterId.toString(),
        state,
        zip,
      })}`,
    });
  }

  const bookSitter = (sitterId: number): void => verification.checkVerification(() => continueToBook(sitterId), openIdentityCheckModal);

  return (
    <div className='grid bg-transparent'>
      <div className='font-bold mb-2.5'>
        <FontAwesomeIcon icon={faHeartCircleCheck} className='inline mr-2 text-bubblegum' />
        {t('dashboard.widgets.favorites.label')}{ !!count && ` (${count})`}
      </div>
      {!sitters.length && !loading ? (
        <FavoritesNullState imageUrls={imageUrls} navigateToSearchPage={navigateToSearchPage} />
      ) : (
        <>
          <div className='grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-x-4 gap-y-8 mb-10'>
            {loading ? SKELETONS : showSitters.map(sitter => (
              <div
                className='m-b-10'
                key={`${sitter.firstName}-${sitter.id}`}>
                <SitterCard
                  sitter={sitter}
                  handleClick={handleClick}
                  trackingCallback={trackEvent}
                  hideDetails
                  setFavorite={setFavorite}
                  loading={loading}
                />
                <div className='flex flex-col w-full'>
                  <Button
                    buttonType={ButtonType.tertiary}
                    className='!py-1 w-full text-left'
                    onClick={(): void => {
                      const message = (): void => navigate(generatePath(Routes.parent.messageSitter, { id: user.id, sitterId: sitter.id }));

                      verification.checkVerification(message, openIdentityCheckModal);
                    }}
                  >
                    {t('dashboard.widgets.favorites.message')}
                    <FontAwesomeIcon icon={faAngleRight} className='pl-2 m-[-1px]' />
                  </Button>
                  <Button
                    buttonType={ButtonType.tertiary}
                    className='!py-1 w-full text-left'
                    onClick={(): void => navigate(generatePath(Routes.parent.sitterCalendar, { sitterId: sitter.id }))}
                  >
                    {t('dashboard.widgets.favorites.checkAvailability')}
                    <FontAwesomeIcon icon={faAngleRight} className='pl-2 m-[-1px]' />
                  </Button>
                  <Button
                    buttonType={ButtonType.tertiary}
                    className='!py-1 w-full text-left'
                    onClick={(): void => sitter.id ? bookSitter(sitter.id) : undefined}
                  >
                    {t('dashboard.widgets.favorites.bookNow')}
                    <FontAwesomeIcon icon={faAngleRight} className='pl-2 m-[-1px]' />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {sitters.length > 4 && (
            <div className='flex md:justify-self-end h-fit'>
              <Button
                buttonType={ButtonType.secondary}
                className='w-full md:w-fit'
                onClick={(): void => navigate(generatePath(Routes.parent.yourSitters, { id: user.id }))}
              >
                {t('dashboard.widgets.favorites.seeAllYourFavorites')} <FontAwesomeIcon icon={faAngleRight} />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
